import { RefObject, useEffect, useRef } from "react";

type AnyEvent = MouseEvent | TouchEvent;

function useOnClickOutside<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  handler: (event: AnyEvent) => void,
  ignore?: { current: HTMLElement | null | undefined },
): void {
  const savedHandler = useRef(handler);
  const savedIgnore = useRef(ignore);

  useEffect(() => {
    savedHandler.current = handler;
    savedIgnore.current = ignore;
  });

  useEffect(() => {
    const listener = (event: AnyEvent) => {
      const el = ref?.current;
      const currentIgnore = savedIgnore.current;

      // Do nothing if clicking ref's element or descendent elements
      if (
        !el ||
        el.contains(event.target as Node) ||
        currentIgnore?.current?.contains(event.target as Node)
      ) {
        return;
      }
      savedHandler.current(event);
    };

    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);

    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };

    // Reload only if ref changes
  }, [ref]);
}

export default useOnClickOutside;
